import React, { useCallback, useEffect, useState } from 'react'
import { gql, useQuery } from '@apollo/client'
import { useSnackbar } from 'notistack'
import { DateTime } from 'luxon'
import { GET_COACH_AVAILABILITY_OVERRIDES } from '../custom-availability/queries'
import OneTimeSlotDialog from './one-time-slot-dialog'

export const GET_ADD_TIME_FACILITIES = gql`
  query CoachFacilitiesForAddTime {
    currentUser {
      attributes {
        ... on Coach {
          facilities {
            id
            name
          }
        }
      }
    }
  }
`

// Opens the Add Blocked Time and Add Open Time dialogs from any page, the
// same `OneTimeSlotDialog` the schedule and availability settings use. Render
// `dialogs` once. Facilities and existing open time load on first use, and a
// dialog opens once they are ready because the form reads them on mount. If
// either request fails, the dialog stays closed, the error goes to Raygun, and
// the next click tries again. `isLoading` is true while a clicked dialog waits.
const useAddTimeDialogs = ({ onSuccess } = {}) => {
  const { enqueueSnackbar } = useSnackbar()
  const [openType, setOpenType] = useState(null)
  const [requested, setRequested] = useState(false)
  const {
    data: facilitiesData,
    loading: facilitiesLoading,
    error: facilitiesError,
    refetch: refetchFacilities,
  } = useQuery(GET_ADD_TIME_FACILITIES, {
    skip: !requested,
    notifyOnNetworkStatusChange: true,
  })
  const {
    data: overridesData,
    loading: overridesLoading,
    error: overridesError,
    refetch: refetchOverrides,
  } = useQuery(GET_COACH_AVAILABILITY_OVERRIDES, {
    skip: !requested,
    notifyOnNetworkStatusChange: true,
    variables: { dateRange: { from: DateTime.local().toISODate() } },
  })

  const loadError = facilitiesError || overridesError

  useEffect(() => {
    if (!loadError) return

    window.rg4js &&
      window.rg4js('send', { error: loadError, tags: ['add-time-dialogs'] })
    enqueueSnackbar(
      'We could not load your calendar details. Please try again.',
      {
        variant: 'error',
      },
    )
    setOpenType(null)
  }, [loadError, enqueueSnackbar])

  const open = type => {
    if (facilitiesError) refetchFacilities().catch(() => {})
    if (overridesError) refetchOverrides().catch(() => {})
    setRequested(true)
    setOpenType(type)
  }
  const close = useCallback(() => setOpenType(null), [])

  const facilities = facilitiesData?.currentUser?.attributes?.facilities || []
  const isReady =
    requested && !loadError && !facilitiesLoading && !overridesLoading
  const handleSuccess = () => {
    refetchOverrides()
    if (onSuccess) onSuccess()
  }

  const dialogs = (
    <>
      <OneTimeSlotDialog
        triggerless
        isOpen={isReady && openType === 'block'}
        onClose={close}
        onSuccess={handleSuccess}
        initialType="block"
        facilities={facilities}
      />
      <OneTimeSlotDialog
        triggerless
        isOpen={isReady && openType === 'open'}
        onClose={close}
        onSuccess={handleSuccess}
        initialType="open"
        facilities={facilities}
        existingOverrides={overridesData?.coachAvailabilityOverrides || []}
      />
    </>
  )

  return {
    isLoading: Boolean(openType) && !isReady,
    openBlockedTime: () => open('block'),
    openOpenTime: () => open('open'),
    dialogs,
  }
}

export default useAddTimeDialogs
